import * as React from "react";
import { observer } from "mobx-react";
import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";
import { UitkSpacing } from "uitk-react-spacing";

import { useLocalization } from "@shared-ui/localization-context";

import { LobTitle } from "components/shared/LobTitle/LobTitle";
import { LobSubtitle } from "components/shared/LobSubtitle/LobSubtitle";
import { CarSubLOBs } from "stores/wizard/state/typings";

import { CarsFormView } from "./components/CarsFormView";
import {
  GroundTransportationPWAViewProps,
  GroundTransportPWAView,
} from "./components/GroundTransportPWA/GroundTransportPWAView";
import { WizardCarPWAViewProps } from "./typings";
import { locKeys } from "./components/l10n";

export const WizardCarPWAView: React.FunctionComponent<WizardCarPWAViewProps> = observer(
  ({
    context,
    flexComponent,
    isAARPChecked,
    isDriverAgeInRange,
    isDiffDropOff,
    onAARPChange,
    onDifferentDropOffSelected,
    onDriverAgeInRange,
    onTabOptionSelected,
    selectedTab,
    setIsDifferentDropOff,
    toggleBookRoundtrip,
    wizardState,
    showGTOnly = false,
  }) => {
    const { formatText } = useLocalization();
    const {
      globalWizardState,
      carWizardState,
      carWizardState: { config: carConfig },
      carWizardState: {
        config: { gtConfig: gtConfig },
      },
    } = wizardState;
    const globalConfig = globalWizardState.config;
    const includeGT = carConfig.includeGT;
    const actionForm =
      selectedTab === CarSubLOBs.RENTAL
        ? carConfig.isCarTrawler
          ? carConfig.form.action.replace("{locale}", context.locale.substr(0, 2))
          : carConfig.form.action
        : gtConfig.form.action;

    const setInputsRefs = (ref: React.RefObject<HTMLInputElement>, secondRef?: React.RefObject<HTMLInputElement>) => {
      carWizardState.wizardInputsArray.push(ref);
      if (secondRef) {
        carWizardState.wizardInputsArray.push(secondRef);
      }
    };

    const getLocError = (isLink: boolean) => {
      if (!isLink) {
        return carWizardState.moreThanOneError()
          ? carConfig.form.submitButton.locMultipleWarningsToken!
          : carConfig.form.submitButton.locWarningToken!;
      }

      return carWizardState.moreThanOneError()
        ? carConfig.form.submitButton.locMultipleWarningsLinkToken
        : carConfig.form.submitButton.locWarningLinkToken;
    };

    const onTabSelect = (clickedTab: CarSubLOBs) => {
      onTabOptionSelected(clickedTab);
    };

    const gtProps: Omit<GroundTransportationPWAViewProps, "routeStore"> = {
      carWizardState,
      toggleBookRoundtrip,
      setInputsRefs,
      getLocError,
      gtConfig,
      globalConfig,
    };
    const gtView = <GroundTransportPWAView {...gtProps} />;

    const carView = (
      <CarsFormView
        setInputsRefs={setInputsRefs}
        carWizardState={carWizardState}
        carConfig={carConfig}
        onDriverAgeInRange={onDriverAgeInRange}
        onDifferentDropOffSelected={onDifferentDropOffSelected}
        setIsDifferentDropOff={setIsDifferentDropOff}
        isDriverAgeInRange={isDriverAgeInRange}
        isDiffDropOff={isDiffDropOff}
        getLocError={getLocError}
        onAARPChange={onAARPChange}
        isAARPChecked={isAARPChecked}
        globalConfig={globalConfig}
      />
    );

    return (
      <form
        noValidate={true}
        action={showGTOnly ? gtConfig?.form?.action : actionForm}
        autoComplete="off"
        className="WizardCarPWA"
        id={carConfig.elementId}
        data-fm={flexComponent.fmId}
        data-fm-title-id={flexComponent.fmTitleId}
        data-testid="wizard"
        onSubmit={carWizardState.submit}
      >
        {showGTOnly ? (
          gtView
        ) : (
          <>
            <LobTitle lobState={carWizardState} />
            <LobSubtitle lobState={carWizardState} />
            {includeGT ? (
              <UitkSpacing margin={{ blockstart: "three" }}>
                <UitkExperimentalButtonTabs
                  selectedTab={carWizardState.subLOBState}
                  tabsType={UitkTabsType.NATURAL}
                  onTabSelect={onTabSelect}
                  tabAnchorType={UitkTabAnchorType.BUTTON}
                >
                  <UitkTab
                    name={CarSubLOBs.RENTAL}
                    displayName={formatText(locKeys.carTabTitle)}
                    id="Rental-cars-transportation"
                    targetURI="carsForm"
                  >
                    {carWizardState.subLOBState === CarSubLOBs.RENTAL && carView}
                  </UitkTab>
                  <UitkTab
                    name={CarSubLOBs.GROUND_TRANSPORTATION}
                    displayName={formatText(locKeys.gtTabTitle)}
                    id="Airport-transportation"
                    targetURI="gtForm"
                  >
                    {carWizardState.subLOBState === CarSubLOBs.GROUND_TRANSPORTATION && gtView}
                  </UitkTab>
                </UitkExperimentalButtonTabs>
              </UitkSpacing>
            ) : (
              carView
            )}
          </>
        )}
      </form>
    );
  }
);

export default WizardCarPWAView;
